import type { AppRole } from './types';

/**
 * Where to go after signing in or registering, when a page sent the visitor there on purpose
 * (`?next=/quotation-request`). Only a path inside this site is accepted: anything else would
 * turn the login into a redirect to wherever a crafted link says.
 */
export function safeNextPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return null;
  }

  return value;
}

/** A link that keeps the destination, for the pages that hand the visitor along. */
export function withNext(path: string, next: string | null): string {
  return next ? `${path}?next=${encodeURIComponent(next)}` : path;
}

/** Panel paths, including `/employee`, the old address that now forwards to `/admin`. */
function isPanelPath(path: string): boolean {
  return ['/admin', '/employee'].some((root) => path === root || path.startsWith(`${root}/`));
}

/** Coming back to a login page after signing in is a wasted hop, never a destination. */
function isLoginPath(path: string): boolean {
  return path === '/login' || path === '/account/login';
}

/**
 * Where a session lands after the single login (#271): staff in the panel, buyers in their
 * account. A `next` is honoured only inside the area of the role, so a buyer sent from a panel
 * link does not bounce off it, and staff sent from the quotation form do not end up there.
 * The area is decided by the path alone, so a query or a fragment does not change it.
 */
export function landingPath(role: AppRole, next: string | null): string {
  const path = next?.split(/[?#]/)[0] ?? '';
  const usableNext = next && !isLoginPath(path) ? next : null;

  if (role === 'CUSTOMER') {
    return usableNext && !isPanelPath(path) ? usableNext : '/account';
  }

  return usableNext && isPanelPath(path) ? usableNext : '/admin';
}
